import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconPlus, IconPencil, IconTrash } from '@tabler/icons-react';
import { CARD_SX, FIELD_SX, OUTLINED_BTN_SX, PRIMARY_BTN_SX } from '../../styles/designSystem';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../table';
import { Chip } from '../chip';
import { SmartSelect, type SmartSelectOption } from '../dropdown';
import { useTicketTypeTemplates } from '../../hooks/useTicketTypeTemplates';
import { useCustomerTicketTemplateOverrides } from '../../hooks/useCustomerTicketTemplateOverrides';
import { useCustomerTaskConfigOverrides } from '../../hooks/useCustomerTaskConfigOverrides';
import { useTaskLists } from '../../hooks/useTaskLists';
import { AdditionalFieldEditor } from '../admin/templates/AdditionalFieldEditor';
import { TemplateLineItemEditor } from '../admin/templates/TemplateLineItemEditor';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import { slugify } from '../../utils/slugify';
import type {
  AdditionalField,
  TemplateLineItem,
  TicketTypeTemplate,
  TicketTypeTemplateOverride,
} from '../../types/ticketTypeTemplate';
import type {
  TaskConfigOverrideMode,
  TemplateTaskConfigOverride,
} from '../../types/taskList';
import type { TaskListDefinition, TaskListReference } from '@techgitter/contracts';
import '../admin/EditorCard.css';
import './CustomerTicketTemplateOverridesPanel.css';
import { uniqueCopyKey } from '../../utils/uniqueCopyKey';

interface Props {
  customerId: string;
  customerLabel?: string;
}

/** Inherit = no task-config override; replace/append map to the override modes. */
type EditorMode = 'inherit' | TaskConfigOverrideMode;

/** One customer customization for a ticket type — the union of the two
 *  independent override rows (line-items/fields + task-config) keyed by
 *  ticketTypeId. Either side may be absent. */
interface CustomizationRow {
  ticketTypeId: string;
  templateOverride?: TicketTypeTemplateOverride;
  taskOverride?: TemplateTaskConfigOverride;
}

type EditorState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; ticketTypeId: string };

interface SubmitPayload {
  ticketTypeId: string;
  lineItems: TemplateLineItem[];
  fields: AdditionalField[];
  mode: EditorMode;
  sections: TaskListReference[];
}

/**
 * Customer detail page section — one unified place to customise a global
 * ticket type for this customer: line items, additional fields, and the
 * task template (replace/append the standard task lists). Each customized
 * ticket type is one row; the editor reconciles the two underlying
 * override records (ticket-type-template + task-config) on save.
 */
export const CustomerTicketTemplateOverridesPanel: React.FC<Props> = ({
  customerId,
  customerLabel,
}) => {
  const templateOv = useCustomerTicketTemplateOverrides(customerId);
  const taskOv = useCustomerTaskConfigOverrides(customerId);
  const { templates, isLoading: templatesLoading } = useTicketTypeTemplates();
  const { taskListCatalog, isLoading: taskListsLoading } = useTaskLists();
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  const [mutationError, setMutationError] = useState<string | null>(null);

  const templatesByTicketTypeId = useMemo(() => {
    const map = new Map<string, TicketTypeTemplate>();
    for (const t of templates) {
      if (t.ticketTypeId) map.set(t.ticketTypeId, t);
    }
    return map;
  }, [templates]);

  const rows: CustomizationRow[] = useMemo(() => {
    const map = new Map<string, CustomizationRow>();
    for (const o of templateOv.overrides) {
      map.set(o.ticketTypeId, {
        ...(map.get(o.ticketTypeId) ?? { ticketTypeId: o.ticketTypeId }),
        templateOverride: o,
      });
    }
    for (const o of taskOv.overrides) {
      map.set(o.ticketTypeId, {
        ...(map.get(o.ticketTypeId) ?? { ticketTypeId: o.ticketTypeId }),
        taskOverride: o,
      });
    }
    return [...map.values()];
  }, [templateOv.overrides, taskOv.overrides]);

  const customizedIds = useMemo(
    () => new Set(rows.map((r) => r.ticketTypeId)),
    [rows],
  );

  const availableTemplatesForNew = useMemo(
    () => templates.filter((t) => !customizedIds.has(t.ticketTypeId)),
    [templates, customizedIds],
  );

  const editingRow: CustomizationRow | null =
    editor.mode === 'edit'
      ? rows.find((r) => r.ticketTypeId === editor.ticketTypeId) ?? null
      : null;

  const isLoading = templateOv.isLoading || taskOv.isLoading;
  const loadError = templateOv.error || taskOv.error;

  const handleDelete = async (row: CustomizationRow) => {
    const label =
      templatesByTicketTypeId.get(row.ticketTypeId)?.displayName ??
      row.ticketTypeId;
    if (
      !window.confirm(
        `Remove all customizations for "${label}"? Tickets for this customer will fall back to the global template and task lists.`,
      )
    ) {
      return;
    }
    setMutationError(null);
    try {
      if (row.templateOverride) await templateOv.remove(row.templateOverride.id);
      if (row.taskOverride) await taskOv.remove(row.taskOverride.id);
    } catch (err) {
      setMutationError(
        err instanceof Error ? err.message : 'Failed to remove customization',
      );
    }
  };

  const handleSubmit = async (payload: SubmitPayload) => {
    setMutationError(null);
    const { ticketTypeId, lineItems, fields, mode, sections } = payload;

    // --- Reconcile the ticket-type-template override (line items + fields) ---
    const existingTemplate = templateOv.overrides.find(
      (o) => o.ticketTypeId === ticketTypeId,
    );
    const wantTemplate = lineItems.length > 0 || fields.length > 0;
    if (wantTemplate) {
      const body = {
        templateLineItems: lineItems.length > 0 ? lineItems : null,
        additionalFields: fields.length > 0 ? fields : null,
      };
      if (existingTemplate) await templateOv.update(existingTemplate.id, body);
      else await templateOv.create({ ticketTypeId, ...body });
    } else if (existingTemplate) {
      await templateOv.remove(existingTemplate.id);
    }

    // --- Reconcile the task-config override ---
    const existingTask = taskOv.overrides.find(
      (o) => o.ticketTypeId === ticketTypeId,
    );
    if (mode !== 'inherit') {
      const taskConfig = { mode, sections };
      if (existingTask) await taskOv.update(existingTask.id, { taskConfig });
      else await taskOv.create({ ticketTypeId, taskConfig });
    } else if (existingTask) {
      await taskOv.remove(existingTask.id);
    }

    setEditor({ mode: 'closed' });
  };

  return (
    <Box sx={{ ...CARD_SX, p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
          Ticket Type Customizations
        </Typography>
        <Button
          variant="contained"
          startIcon={<IconPlus size={16} stroke={1.5} />}
          onClick={() => {
            setMutationError(null);
            setEditor({ mode: 'create' });
          }}
          disabled={availableTemplatesForNew.length === 0}
          sx={PRIMARY_BTN_SX}
        >
          Add customization
        </Button>
      </Stack>

      {customerLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Customizations on this page only affect tickets created for{' '}
          <strong>{customerLabel}</strong> — override the line items,
          additional fields, and task lists for a ticket type.
        </Typography>
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {mutationError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setMutationError(null)}
        >
          {mutationError}
        </Alert>
      )}

      {isLoading || templatesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No customizations yet. Click <strong>Add customization</strong> to
          deviate from the global template or task lists for a ticket type.
        </Typography>
      ) : (
        <DataTable>
          <thead>
            <tr>
              <DataTableHeadCell>Ticket Type</DataTableHeadCell>
              <DataTableHeadCell>Customizations</DataTableHeadCell>
              <DataTableHeadCell align="right">Actions</DataTableHeadCell>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const parent = templatesByTicketTypeId.get(row.ticketTypeId);
              const label = parent?.displayName ?? row.ticketTypeId;
              const lineItems = row.templateOverride?.templateLineItems;
              const fields = row.templateOverride?.additionalFields;
              const taskConfig = row.taskOverride?.taskConfig;
              return (
                <DataTableRow key={row.ticketTypeId}>
                  <DataTableCell bold>
                    {label}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.ticketTypeId}
                    </Typography>
                  </DataTableCell>
                  <DataTableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {lineItems != null && (
                        <Chip>{`${lineItems.length} line item(s)`}</Chip>
                      )}
                      {fields != null && (
                        <Chip>{`${fields.length} field(s)`}</Chip>
                      )}
                      {taskConfig != null && (
                        <Chip tone="info">
                          {`tasks: ${taskConfig.mode} ${taskConfig.sections.length}`}
                        </Chip>
                      )}
                      {lineItems == null && fields == null && taskConfig == null && (
                        <Chip>(empty)</Chip>
                      )}
                    </Stack>
                  </DataTableCell>
                  <DataTableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setMutationError(null);
                          setEditor({
                            mode: 'edit',
                            ticketTypeId: row.ticketTypeId,
                          });
                        }}
                      >
                        <IconPencil size={16} stroke={1.5} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleDelete(row)}>
                        <IconTrash size={16} stroke={1.5} />
                      </IconButton>
                    </Tooltip>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        </DataTable>
      )}

      <CustomizationEditorDialog
        state={editor}
        availableTemplates={availableTemplatesForNew}
        templatesByTicketTypeId={templatesByTicketTypeId}
        taskLists={taskListCatalog.taskLists}
        taskListsLoading={taskListsLoading}
        editingRow={editingRow}
        onClose={() => setEditor({ mode: 'closed' })}
        onSubmit={async (payload) => {
          try {
            await handleSubmit(payload);
          } catch (err) {
            setMutationError(
              err instanceof Error ? err.message : 'Failed to save customization',
            );
          }
        }}
      />
    </Box>
  );
};

// ----------------------------------------------------------------------------
// Editor dialog
// ----------------------------------------------------------------------------

interface EditorDialogProps {
  state: EditorState;
  availableTemplates: TicketTypeTemplate[];
  templatesByTicketTypeId: Map<string, TicketTypeTemplate>;
  taskLists: TaskListDefinition[];
  taskListsLoading: boolean;
  editingRow: CustomizationRow | null;
  onClose: () => void;
  onSubmit: (payload: SubmitPayload) => Promise<void> | void;
}

const CustomizationEditorDialog: React.FC<EditorDialogProps> = ({
  state,
  availableTemplates,
  templatesByTicketTypeId,
  taskLists,
  taskListsLoading,
  editingRow,
  onClose,
  onSubmit,
}) => {
  const isOpen = state.mode !== 'closed';
  const isEdit = state.mode === 'edit';
  const lockedTicketTypeId = isEdit ? state.ticketTypeId : '';

  const [ticketTypeId, setTicketTypeId] = useState('');
  const [lineItems, setLineItems] = useState<TemplateLineItem[]>([]);
  const [fields, setFields] = useState<AdditionalField[]>([]);
  const [mode, setMode] = useState<EditorMode>('inherit');
  const [selectedTaskListIds, setSelectedTaskListIds] = useState<string[]>([]);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newLineItemKey, setNewLineItemKey] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Shared catalog for the Line Item ID picker (same source the ticket
  // line-item picker uses); passed down to each line-item editor below.
  const { products } = useProductCatalog();

  // Re-seed inputs whenever the dialog opens for a different row.
  useEffect(() => {
    if (!isOpen) return;
    const templateOverride = editingRow?.templateOverride;
    const taskOverride = editingRow?.taskOverride;
    setTicketTypeId(isEdit ? lockedTicketTypeId : '');
    setLineItems(
      (templateOverride?.templateLineItems as TemplateLineItem[] | null) ?? [],
    );
    setFields(
      (templateOverride?.additionalFields as AdditionalField[] | null) ?? [],
    );
    setMode(taskOverride?.taskConfig?.mode ?? 'inherit');
    setSelectedTaskListIds(
      taskOverride?.taskConfig
        ? taskOverride.taskConfig.sections
            .filter(
              (s): s is TaskListReference =>
                typeof s === 'object' && s != null && 'taskListId' in s,
            )
            .map((s) => s.taskListId)
        : [],
    );
    setNewFieldLabel('');
    setNewFieldKey('');
    setNewLineItemKey('');
    setLocalError(null);
  }, [isOpen, isEdit, lockedTicketTypeId, editingRow]);

  const parentTemplate =
    state.mode === 'create'
      ? templatesByTicketTypeId.get(ticketTypeId)
      : isEdit
        ? templatesByTicketTypeId.get(lockedTicketTypeId)
        : undefined;

  // --- Additional field array helpers (mirrors TicketTypeTemplateDetailEditor) ---
  const addField = () => {
    const label = newFieldLabel.trim();
    const key = newFieldKey.trim() || slugify(label).replace(/-/g, '');
    if (!label || !key || fields.some((f) => f.fieldKey === key)) return;
    setFields([...fields, { fieldKey: key, label, type: 'text', required: 'optional' }]);
    setNewFieldLabel('');
    setNewFieldKey('');
  };
  const updateField = (index: number, updated: AdditionalField) =>
    setFields(fields.map((f, i) => (i === index ? updated : f)));
  const removeField = (index: number) =>
    setFields(fields.filter((_, i) => i !== index));
  const duplicateField = (index: number) => {
    const source = fields[index];
    const key = uniqueCopyKey(source.fieldKey, (k) => fields.some((f) => f.fieldKey === k));
    const next = [...fields];
    next.splice(index + 1, 0, { ...source, fieldKey: key });
    setFields(next);
  };
  const moveField = (index: number, dir: 'up' | 'down') => {
    const target = dir === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    setFields(next);
  };

  // --- Line item array helpers ---
  const addLineItem = () => {
    const key = newLineItemKey.trim() || `item_${lineItems.length + 1}`;
    if (lineItems.some((li) => li.templateKey === key)) return;
    setLineItems([
      ...lineItems,
      { templateKey: key, lineItemId: '', category: 'labour', defaultQuantity: 1 },
    ]);
    setNewLineItemKey('');
  };
  const updateLineItem = (index: number, updated: TemplateLineItem) =>
    setLineItems(lineItems.map((li, i) => (i === index ? updated : li)));
  const removeLineItem = (index: number) =>
    setLineItems(lineItems.filter((_, i) => i !== index));
  const duplicateLineItem = (index: number) => {
    const source = lineItems[index];
    const key = uniqueCopyKey(source.templateKey, (k) =>
      lineItems.some((li) => li.templateKey === k),
    );
    const next = [...lineItems];
    next.splice(index + 1, 0, { ...source, templateKey: key });
    setLineItems(next);
  };
  const moveLineItem = (index: number, dir: 'up' | 'down') => {
    const target = dir === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= lineItems.length) return;
    const next = [...lineItems];
    [next[index], next[target]] = [next[target], next[index]];
    setLineItems(next);
  };

  const handleSeedFromParent = () => {
    if (!parentTemplate) return;
    if (parentTemplate.templateLineItems) setLineItems([...parentTemplate.templateLineItems]);
    if (parentTemplate.additionalFields) setFields([...parentTemplate.additionalFields]);
  };

  const toggleTaskList = (taskListId: string) => {
    setSelectedTaskListIds((prev) =>
      prev.includes(taskListId)
        ? prev.filter((id) => id !== taskListId)
        : [...prev, taskListId],
    );
  };

  const handleSubmit = async () => {
    const effectiveTicketTypeId = isEdit ? lockedTicketTypeId : ticketTypeId;
    if (!effectiveTicketTypeId) {
      setLocalError('Pick a ticket type to customize');
      return;
    }
    if (mode !== 'inherit' && selectedTaskListIds.length === 0) {
      setLocalError(`Select at least one task list to ${mode} the global list`);
      return;
    }
    if (lineItems.length === 0 && fields.length === 0 && mode === 'inherit') {
      setLocalError(
        'Add at least one line item, field, or task-list override',
      );
      return;
    }

    // Build sections in catalog order so the checklist order is stable.
    const sections: TaskListReference[] =
      mode === 'inherit'
        ? []
        : taskLists
            .filter((tl) => selectedTaskListIds.includes(tl.taskListId))
            .map((tl) => ({ taskListId: tl.taskListId }));

    setSubmitting(true);
    try {
      await onSubmit({
        ticketTypeId: effectiveTicketTypeId,
        lineItems,
        fields,
        mode,
        sections,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const dialogTitle = isEdit
    ? `Edit customization: ${
        templatesByTicketTypeId.get(lockedTicketTypeId)?.displayName ??
        lockedTicketTypeId
      }`
    : 'Add ticket type customization';

  const ticketTypeOptions: SmartSelectOption[] = availableTemplates.map((t) => ({
    value: t.ticketTypeId,
    label: `${t.displayName} (${t.ticketTypeId})`,
  }));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: 'cust-ttc-dialog' }}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {state.mode === 'create' && (
            <Box>
              <SmartSelect
                label="Ticket Type"
                placeholder={
                  availableTemplates.length === 0
                    ? 'All ticket types already have a customization'
                    : 'Select…'
                }
                value={ticketTypeId || null}
                options={ticketTypeOptions}
                onChange={(v) => setTicketTypeId(v ?? '')}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Pick a global ticket type to customize for this customer
              </Typography>
            </Box>
          )}

          {/* --- Task template --- */}
          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}
            >
              Task template
            </Typography>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value as EditorMode)}
            >
              <FormControlLabel
                value="inherit"
                control={<Radio size="small" />}
                label="Inherit global"
              />
              <FormControlLabel
                value="replace"
                control={<Radio size="small" />}
                label="Replace global"
              />
              <FormControlLabel
                value="append"
                control={<Radio size="small" />}
                label="Append to global"
              />
            </RadioGroup>
            {mode !== 'inherit' && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {mode === 'replace'
                    ? 'These task lists replace the standard ones for this ticket type.'
                    : 'These task lists are added after the standard ones.'}
                </Typography>
                {taskListsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : taskLists.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No task lists are defined yet.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflowY: 'auto',
                      border: '0.5px solid #e2eaf3',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.5,
                    }}
                  >
                    {taskLists.map((tl) => (
                      <FormControlLabel
                        key={tl.taskListId}
                        sx={{ display: 'flex' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedTaskListIds.includes(tl.taskListId)}
                            onChange={() => toggleTaskList(tl.taskListId)}
                          />
                        }
                        label={
                          <span>
                            {tl.displayName}{' '}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              ({tl.taskListId})
                            </Typography>
                          </span>
                        }
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* --- Line items + additional fields --- */}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                Line items &amp; fields
              </Typography>
              {parentTemplate && (
                <Button size="small" onClick={handleSeedFromParent}>
                  Seed from global template
                </Button>
              )}
            </Stack>

            <Box sx={{ px: '2px', pt: '4px' }}>
              <span className="editor-section-title">
                Additional Fields ({fields.length})
              </span>
            </Box>
            {fields.length === 0 && (
              <div className="editor-empty">No field overrides.</div>
            )}
            {fields.map((field, index) => (
              <AdditionalFieldEditor
                key={field.fieldKey}
                field={field}
                siblingFieldKeys={fields.map((f) => f.fieldKey)}
                onChange={(updated) => updateField(index, updated)}
                onDelete={() => removeField(index)}
                onDuplicate={() => duplicateField(index)}
                onMoveUp={index > 0 ? () => moveField(index, 'up') : undefined}
                onMoveDown={
                  index < fields.length - 1 ? () => moveField(index, 'down') : undefined
                }
              />
            ))}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="New field label"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                sx={[{ flex: 1 }, FIELD_SX]}
              />
              <TextField
                size="small"
                label="Field key"
                value={newFieldKey}
                onChange={(e) => setNewFieldKey(e.target.value)}
                sx={[{ width: 180 }, FIELD_SX]}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                placeholder={newFieldLabel ? slugify(newFieldLabel).replace(/-/g, '') : undefined}
              />
              <Button
                variant="outlined"
                startIcon={<IconPlus size={16} stroke={1.5} />}
                onClick={addField}
                disabled={!newFieldLabel.trim()}
                sx={[OUTLINED_BTN_SX, { mt: '1px', flexShrink: 0 }]}
              >
                Add
              </Button>
            </Stack>

            <Box sx={{ px: '2px', pt: '12px' }}>
              <span className="editor-section-title">
                Template Line Items ({lineItems.length})
              </span>
            </Box>
            {lineItems.length === 0 && (
              <div className="editor-empty">No line item overrides.</div>
            )}
            {lineItems.map((item, index) => (
              <TemplateLineItemEditor
                key={item.templateKey}
                item={item}
                products={products}
                onChange={(updated) => updateLineItem(index, updated)}
                onDelete={() => removeLineItem(index)}
                onDuplicate={() => duplicateLineItem(index)}
                onMoveUp={index > 0 ? () => moveLineItem(index, 'up') : undefined}
                onMoveDown={
                  index < lineItems.length - 1 ? () => moveLineItem(index, 'down') : undefined
                }
              />
            ))}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="New line item template key"
                value={newLineItemKey}
                onChange={(e) => setNewLineItemKey(e.target.value)}
                sx={[{ flex: 1 }, FIELD_SX]}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                placeholder={`item_${lineItems.length + 1}`}
              />
              <Button
                variant="outlined"
                startIcon={<IconPlus size={16} stroke={1.5} />}
                onClick={addLineItem}
                sx={[OUTLINED_BTN_SX, { mt: '1px', flexShrink: 0 }]}
              >
                Add
              </Button>
            </Stack>
          </Box>

          {localError && <Alert severity="error">{localError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ color: '#aaaaaa', '&:hover': { color: '#555555', bgcolor: 'transparent' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          sx={PRIMARY_BTN_SX}
        >
          {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
